import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAttentionPoll } from "../lib/useAttentionPoll";
import { useClientContext } from "../lib/ClientContext";
import { useSelectedMonth } from "../lib/useSelectedMonth";
import { AttentionCategory } from "../components/AttentionCategory";
import { RotationQuickFix } from "../components/RotationQuickFix";
import { api } from "../lib/api";

export function TriageInboxPage() {
  const { month, setMonth } = useSelectedMonth();
  // Modal-route handoff: clicking a row from the inbox opens the detail in
  // an overlay so the inbox scroll position survives. Direct links unchanged.
  const location = useLocation();
  const linkState = { background: location };
  const [batchBusy, setBatchBusy] = useState(false);
  const { clients, loaded: clientsLoaded, selectedClientId: clientId, setSelectedClientId: setClientId } = useClientContext();
  const { data, loading, error, refresh } = useAttentionPoll(month, clientId);

  const runBatchValidate = async () => {
    if (batchBusy || data.batch_ready_greenlight.count === 0) return;
    setBatchBusy(true);
    try {
      // Attention-query returns mixed ts+ex ids; validator needs them partitioned by kind.
      // We don't have kind in the greenlight list — fetch card details or split via /api/cards.
      // Simpler: ask backend for the split. For now we try both kinds and let errors fall through.
      const ids = data.batch_ready_greenlight.ids;
      const tsIds = ids.slice(0, ids.length);  // best-effort — backend filters by ocr_complete anyway
      const r = await api.batch.validate("timesheet", tsIds);
      const r2 = await api.batch.validate("expense", tsIds);
      alert(`Validiert: ${r.validated + r2.validated} · Fehler: ${r.errors.length + r2.errors.length}`);
      refresh();
    } catch (e: any) {
      alert(`Fehler: ${e.message}`);
    } finally {
      setBatchBusy(false);
    }
  };

  if (clientsLoaded && clients.length === 0) {
    return (
      <div className="inbox-page">
        <div className="empty-state">
          <h3>Kein aktiver Mandant</h3>
          <p className="muted">Bitte zuerst in den Einstellungen einen Mandanten anlegen.</p>
          <Link to="/settings" className="primary" style={{ textDecoration: "none", padding: "8px 14px", display: "inline-block", borderRadius: 6 }}>
            Zu den Einstellungen
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="inbox-page">
      <header className="inbox-header">
        <label className="field-inline">
          <span>Mandant</span>
          <select value={clientId ?? ""} onChange={(e) => setClientId(e.target.value)}>
            {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
        </label>
        <label className="field-inline">
          <span>Monat</span>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </label>
        <Link to={`/board?month=${month}`} className="link">Kanban-Ansicht →</Link>
        {loading && <span className="muted">Lade…</span>}
        {error && <span className="error">Fehler: {error}</span>}
      </header>

      {data.rotation_failures.length > 0 && (
        <AttentionCategory
          variant="error"
          icon="↻"
          title="Drehung konnte nicht erkannt werden"
          subtitle={`${data.rotation_failures.length} ${data.rotation_failures.length === 1 ? "Karte braucht" : "Karten brauchen"} Deine Entscheidung`}
        >
          {data.rotation_failures.map((item) => (
            <RotationQuickFix key={item.card_id} item={item} onResolved={refresh} />
          ))}
        </AttentionCategory>
      )}

      {data.classification_unclear.length > 0 && (
        <AttentionCategory
          variant="warning"
          icon="❓"
          title="Unklare Seiten"
          subtitle={`${data.classification_unclear.length} ${data.classification_unclear.length === 1 ? "Seite wartet" : "Seiten warten"} auf Klassifizierung (Stundenliste · Diäten · verwerfen)`}
        >
          {data.classification_unclear.map((item) => (
            <div key={item.card_id} className="attention-row">
              <span className="attention-row-main">
                📄 {item.filename}{item.source_page_index != null && <span className="muted"> · Seite {item.source_page_index}</span>}
              </span>
              <Link to={`/expenses/${item.card_id}`} state={linkState} className="link">öffnen →</Link>
            </div>
          ))}
        </AttentionCategory>
      )}

      {data.assignment_missing.length > 0 && (
        <AttentionCategory
          variant="warning"
          icon="👤"
          title="Mitarbeiter-Zuordnung fehlt"
          subtitle={`${data.assignment_missing.length} ${data.assignment_missing.length === 1 ? "Karte ist" : "Karten sind"} fertig gescannt aber nicht zugeordnet`}
        >
          {data.assignment_missing.map((item) => (
            <div key={item.card_id} className="attention-row">
              <span className="attention-row-main">
                📄 {item.filename}{item.source_page_index != null && <span className="muted"> · Seite {item.source_page_index}</span>}
                {item.employee_raw && <span className="muted"> · OCR-Header: »{item.employee_raw.slice(0, 40)}«</span>}
              </span>
              <Link to={item.kind === "timesheet" ? `/timesheets/${item.card_id}` : `/expenses/${item.card_id}`} state={linkState} className="link">zuordnen →</Link>
            </div>
          ))}
        </AttentionCategory>
      )}

      {data.ocr_quality_low.length > 0 && (
        <AttentionCategory
          variant="warning"
          icon="⚠"
          title="OCR-Qualität zu niedrig"
          subtitle={`${data.ocr_quality_low.length} ${data.ocr_quality_low.length === 1 ? "Karte hat" : "Karten haben"} eine Erkennungsrate unter der Schwelle`}
        >
          {data.ocr_quality_low.map((item) => (
            <div key={item.card_id} className="attention-row">
              <span className="attention-row-main">
                📄 {item.filename} <span className="muted">· Score {Math.round(item.score * 100)}% (Schwelle {Math.round(item.threshold * 100)}%)</span>
              </span>
              <Link to={item.kind === "timesheet" ? `/timesheets/${item.card_id}` : `/expenses/${item.card_id}`} state={linkState} className="link">prüfen →</Link>
            </div>
          ))}
        </AttentionCategory>
      )}

      {data.pending_conflict.length > 0 && (
        <AttentionCategory
          variant="error"
          icon="⚡"
          title="Konflikt — zwei Karten für denselben Mitarbeiter"
          subtitle={`${data.pending_conflict.length} ${data.pending_conflict.length === 1 ? "Konflikt" : "Konflikte"} müssen aufgelöst werden`}
        >
          {data.pending_conflict.map((item) => (
            <div key={item.card_id} className="attention-row">
              <span className="attention-row-main">
                📄 {item.filename}
                {item.last_name && <span className="muted"> · {item.last_name}, {item.first_name}</span>}
              </span>
              <Link to={`/conflicts/${item.kind}/${item.card_id}`} className="link">auflösen →</Link>
            </div>
          ))}
        </AttentionCategory>
      )}

      {data.retry_stuck.length > 0 && (
        <AttentionCategory
          variant="info"
          icon="↻"
          title="Im Retry — System versucht es erneut"
          subtitle="Normal bei Hardware-Engpässen; Karten werden automatisch wieder aufgenommen"
        >
          {data.retry_stuck.map((item) => (
            <div key={item.job_id} className="attention-row">
              <span className="attention-row-main muted">
                Job {item.job_id.slice(0, 8)} · {item.retry_count} Retries
                {item.last_error && <span> · {item.last_error.slice(0, 60)}</span>}
              </span>
            </div>
          ))}
        </AttentionCategory>
      )}

      {data.batch_ready_greenlight.count > 0 && (
        <AttentionCategory
          variant="success"
          icon="✓"
          title="Bereit zum Validieren"
          subtitle={`${data.batch_ready_greenlight.count} Karten sind vollständig erkannt + zugeordnet`}
        >
          <div className="attention-row">
            <span className="attention-row-main muted">
              Alle grünen Karten in einem Klick validieren
            </span>
            <button
              type="button"
              className="primary success"
              disabled={batchBusy}
              onClick={runBatchValidate}
            >
              {batchBusy ? "…" : `${data.batch_ready_greenlight.count} validieren`}
            </button>
          </div>
        </AttentionCategory>
      )}

      {data.is_empty && (
        <div className="empty-state">
          <h3>🎉 Alles erledigt</h3>
          <p className="muted">Keine offenen Aktionen für {month}. Upload eines PDFs startet die Pipeline.</p>
          {data.months_with_work.length > 0 && (
            <div className="empty-state-hint">
              <p className="muted">Andere Monate mit offenen Karten:</p>
              <div className="month-hint-chips">
                {data.months_with_work.map((m) => (
                  <button
                    key={m.month}
                    type="button"
                    className="month-hint-chip"
                    onClick={() => setMonth(m.month)}
                  >
                    {m.month} · <strong>{m.open_count}</strong>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="system-status-strip">
        {data.in_progress.ocr_pending > 0 && <span>⏳ {data.in_progress.ocr_pending} warten</span>}
        {data.in_progress.ocr_running > 0 && <span>🔄 {data.in_progress.ocr_running} laufen</span>}
        {data.done.validated > 0 && <span>✓ {data.done.validated} validiert</span>}
        {data.done.calculated > 0 && <span>∑ {data.done.calculated} berechnet</span>}
        {data.done.exported > 0 && <span>📊 {data.done.exported} exportiert</span>}
      </div>
    </div>
  );
}
